import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UserCache } from 'src/app/services/user-cache';
import { LocalPrintService } from 'src/app/services/local-print.service';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { GetWeekText } from 'src/app/shared/utilities';
import { ValueTextPair, ValueTextPairNumberValue } from 'src/app/shared/models/value-text-pair';

export interface MonthScheduleReportItem {
  Year: number;
  Month: number;
  Day: number;
  SecTime: number;
  RoomCode: string;
  PositionCode: string;
  UserId: number;
  UserName: string;
  DefaPharm?: number;
}

@Component({
  selector: 'app-month-schedule-report',
  templateUrl: './month-schedule-report.component.html',
  styleUrls: ['./month-schedule-report.component.css']
})
export class MonthScheduleReportComponent implements OnInit {

  @ViewChild('reportContainer')
  reportContainer: ElementRef<HTMLDivElement>;

  @Output()
  printed = new EventEmitter<boolean>();

  week: ValueTextPairNumberValue[] = GetWeekText();

  clinicName: string = UserCache.getLoginUser().ClinicName;
  reportTitle: string = '';

  year: number;
  month: number;

  constructor(
    private localPrintService: LocalPrintService,
    private notification: EasyNotificationService) {
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.reportContainer.nativeElement.innerHTML = this.buildHeaderOnlyHtml();
  }

  isDoctor(code: string): boolean {
    return ['10', '11'].includes(code);
  }
  isPhar(code: string): boolean {
    return ['40', '41', '42'].includes(code);
  }
  getPosColor(code: string): string {
    return this.isDoctor(code) ? '#06c' : this.isPhar(code) ? '#fab415' : 'gray';
  }
  getPosName(code: string): string {
    switch (code) {
      case '10': return '專任';
      case '11': return '非專';
      case '40': return '藥師';
      case '41': return '藥生';
      case '42': return '支藥';
      default: return '';
    }
  }

  showRoomText(item: MonthScheduleReportItem, rooms: ValueTextPair[]): string {
    if (!item.RoomCode) {
      return this.isPhar(item.PositionCode) ? '不分' : '';
    }
    const room = rooms.find(r => r.value === item.RoomCode);
    return room ? room.text : '';
  }
  showSecText(item: MonthScheduleReportItem, sectorOfDay: ValueTextPair[]): string {
    const sec = sectorOfDay.find(s => Number(s.value) === item.SecTime);
    return sec ? sec.text : '';
  }
  getPharName(id: number, pharmacists: ValueTextPairNumberValue[]): string {
    if (!id) return '';
    const p = pharmacists.find(x => x.value === id);
    return p ? p.text : '';
  }

  buildHeaderOnlyHtml(): string {
    let ths = '';
    this.week.forEach(w => {
      ths += `<th style="border:1px solid #333; padding:6px; font-size:16px; font-family:'細明體'; background-color:#f5f5f5; width:${(100 / this.week.length).toFixed(2)}%;">${w.text}</th>`;
    });
    return `
      <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
        <thead><tr>${ths}</tr></thead>
        <tbody></tbody>
      </table>`;
  }

  buildDayCellHtml(
    date: Date,
    dayItems: MonthScheduleReportItem[],
    sectorOfDay: ValueTextPair[],
    rooms: ValueTextPair[],
    pharmacists: ValueTextPairNumberValue[]
  ): string {
    const dayNum = date.getDate() < 10 ? '0' + date.getDate() : date.getDate().toString();
    let content = `<div style="font-weight:bold; text-align:left; font-size:13px; font-family:'細明體';">${dayNum}</div>`;

    // 排序：時段 > 診間 > 職稱
    const sorted = [...dayItems].sort((a, b) => {
      if (a.SecTime !== b.SecTime) return a.SecTime - b.SecTime;
      if (a.RoomCode !== b.RoomCode) return a.RoomCode > b.RoomCode ? 1 : -1;
      return a.PositionCode > b.PositionCode ? 1 : -1;
    });

    let lastSecTime: number = null;
    sorted.forEach(item => {
      if (item.SecTime !== lastSecTime) {
        const secText = this.showSecText(item, sectorOfDay);
        content += `<div style="margin-top:3px; font-size:12px; font-family:'細明體';">${secText}-</div>`;
        lastSecTime = item.SecTime;
      }
      const roomText = this.showRoomText(item, rooms);
      const color = this.getPosColor(item.PositionCode);
      const posName = this.getPosName(item.PositionCode);
      const phName = this.getPharName(item.DefaPharm, pharmacists);
      content += `
        <div style="padding-left:8px; white-space:nowrap; font-size:12px; font-family:'細明體'; color:${color};">
          ${roomText}${item.UserName}(${posName})${phName ? ' 藥:' + phName : ''}
        </div>`;
    });

    return `<td style="border:1px solid #333; vertical-align:top; padding:4px; height:110px;">${content}</td>`;
  }

  buildCalendarHtml(
    weeks: (Date | null)[][],
    itemsByDay: Map<string, MonthScheduleReportItem[]>,
    sectorOfDay: ValueTextPair[],
    rooms: ValueTextPair[],
    pharmacists: ValueTextPairNumberValue[]
  ): string {
    let headerCells = '';
    this.week.forEach(w => {
      headerCells += `<th style="border:1px solid #333; padding:6px; font-size:16px; font-family:'細明體'; background-color:#f5f5f5; width:${(100 / this.week.length).toFixed(2)}%;">${w.text}</th>`;
    });

    let bodyRows = '';
    weeks.forEach(week => {
      let cells = '';
      week.forEach(date => {
        if (!date) {
          cells += `<td style="border:1px solid #333; vertical-align:top; padding:4px; height:110px;"></td>`;
        } else {
          const key = this.getDayKey(date);
          const items = itemsByDay.get(key) || [];
          cells += this.buildDayCellHtml(date, items, sectorOfDay, rooms, pharmacists);
        }
      });
      bodyRows += `<tr style="height:110px;">${cells}</tr>`;
    });

    return `
      <div style="margin:0 auto; width:21.59cm; padding:0.4cm 0.4cm; box-sizing:border-box;">
        <div style="text-align:center; font-size:20px; font-weight:bold; padding:4px 0; margin-bottom:2px; font-family:'細明體';">
          ${this.reportTitle}
        </div>
        <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>`;
  }

  getHtml() {
    if (!this.reportContainer) return '';
    return this.reportContainer.nativeElement.innerHTML;
  }

  async onPrint() {
    const printContents = this.getHtml();
    var ret = await this.localPrintService.printHtml(444, this.reportTitle, printContents);
    if (ret.Successed) {
      this.notification.showSuccess(this.reportTitle + '已送出列印!', true);
    } else {
      this.notification.showError(this.reportTitle + '送出列印失敗!');
    }
    this.printed.emit(true);
  }

  fillData(
    year: number,
    month: number,
    schedules: MonthScheduleReportItem[],
    sectorOfDay: ValueTextPair[],
    rooms: ValueTextPair[],
    pharmacists: ValueTextPairNumberValue[] = []
  ) {
    console.log('fillData', year, month, schedules, sectorOfDay, rooms, pharmacists);
    this.year = year;
    this.month = month;

    const rocYear = year - 1911;
    const monthStr = (month < 10 ? '0' + month : month.toString());
    this.reportTitle = rocYear.toString() + monthStr + ' 月排班表';

    // 依日期分組
    const itemsByDay = new Map<string, MonthScheduleReportItem[]>();
    schedules
      .filter(s => s.Year === year && s.Month === month)
      .forEach(s => {
        const key = s.Year + '-' + (s.Month - 1) + '-' + s.Day;
        if (!itemsByDay.has(key)) itemsByDay.set(key, []);
        itemsByDay.get(key).push(s);
      });

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const totalDaysInMonth = lastDay.getDate();
    const startWeekday = firstDay.getDay();   // 0=日 6=六

    const weeks: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    for (let i = 0; i < startWeekday; i++) {
      currentWeek.push(null);
    }

    for (let day = 1; day <= totalDaysInMonth; day++) {
      currentWeek.push(new Date(year, month - 1, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    this.reportContainer.nativeElement.innerHTML =
      this.buildCalendarHtml(weeks, itemsByDay, sectorOfDay, rooms, pharmacists);
  }

  private getDayKey(d: Date): string {
    return d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
  }
}